/**
 * Core Events Logger
 *
 * Handles logging of core application events like screen views to PostHog Analytics.
 * @module features/analytics/utils/coreEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  TLogScreenViewParams,
  TPageNotFoundParams,
  ICoreAnalyticsService,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Core events analytics logger.
 *
 * Provides methods for logging fundamental application events.
 * @example
 * ```typescript
 * import { coreEventLogger } from '@/features/analytics';
 *
 * coreEventLogger.logScreenView({
 *   screen_name: 'Home',
 *   screen_class: 'HomeScreen'
 * });
 * ```
 */
export const coreEventLogger: ICoreAnalyticsService = {
  /**
   * Logs a screen view event.
   * @param params - Screen view parameters including screen name and class
   */
  logScreenView: async (params: TLogScreenViewParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.CORE.SCREEN_VIEW, params);
  },

  /**
   * Logs a page-not-found event for an unmatched route.
   * @param params - The attempted (unmatched) path
   */
  logPageNotFound: async (params: TPageNotFoundParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.CORE.NOT_FOUND, params);
  },
};
